import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from "react";
import { CookieConsentContextType, ConsentState } from "./cookieTypes";
import { loadConsent, saveConsent } from "./cookieStorage";

const defaultConsent: ConsentState = {
	necessary: true,
	functional: false,
	analytics: false,
	marketing: false,
};

const CookieConsentContext = createContext<
	CookieConsentContextType | undefined
>(undefined);

interface CookieConsentProviderProps {
	children: ReactNode;
}

export function CookieConsentProvider({
	children,
}: CookieConsentProviderProps) {
	const [consent, setConsentState] = useState<ConsentState | null>(null);

	useEffect(() => {
		const storedConsent = loadConsent();
		setConsentState(storedConsent);
	}, []);

	const setConsent = (newConsent: ConsentState) => {
		setConsentState(newConsent);
		saveConsent(newConsent);
	};

	const hasConsent = (category: keyof ConsentState): boolean => {
		if (!consent) return false;
		return consent[category];
	};

	return (
		<CookieConsentContext.Provider
			value={{
				consent,
				hasConsent,
				setConsent,
			}}
		>
			{children}
		</CookieConsentContext.Provider>
	);
}

export function useCookieConsent(): CookieConsentContextType {
	const ctx = useContext(CookieConsentContext);
	if (!ctx) {
		throw new Error(
			"useCookieConsent must be used within a CookieConsentProvider"
		);
	}
	return ctx;
}
